import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || 'http://localhost:5007';
const TOKEN_KEY = 'siti_web_token';
const USER_KEY = 'siti_web_user';

function normalizeUser(usuario) {
  if (!usuario) {
    return null;
  }

  return {
    id: usuario.id,
    nome: usuario.nome,
    usuario: usuario.usuario || usuario.nome,
    email: usuario.email || null,
    filial: usuario.filial || null,
    setor: usuario.setor || null,
    perfil_id: usuario.perfil_id || null,
    perfil_nome: usuario.perfil_nome || null,
    permissoes: Array.isArray(usuario.permissoes) ? usuario.permissoes : [],
    // Multicanal v2: canal padrao + canais extras visiveis + flag de assumir
    // nos extras. ve_todos_canais mantido como compat (token antigo).
    canal_id: usuario.canal_id ?? null,
    canais_extra: Array.isArray(usuario.canais_extra) ? usuario.canais_extra : [],
    assumir_outros: !!usuario.assumir_outros,
    ve_todos_canais: !!usuario.ve_todos_canais,
  };
}

const INVALID_TOKEN_REGEX = /token\s+(inv[aá]lido|expirado|n[aã]o\s+fornecido)/i;

function isInvalidTokenPayload(data) {
  if (!data || typeof data !== 'object') {
    return false;
  }
  if (data.success === false && typeof data.message === 'string' && INVALID_TOKEN_REGEX.test(data.message)) {
    return true;
  }
  return false;
}

let sessionExpiredHandling = false;

function handleSessionExpired() {
  if (sessionExpiredHandling) {
    return;
  }
  sessionExpiredHandling = true;

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  delete axios.defaults.headers.common.Authorization;

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  } else {
    sessionExpiredHandling = false;
  }
}

function normalizeAuthPayload(payload) {
  if (payload?.data?.token && payload?.data?.usuario) {
    return payload.data;
  }

  if (payload?.token && payload?.usuario) {
    return payload;
  }

  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);

    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem(USER_KEY);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => {
        if (isInvalidTokenPayload(response?.data)) {
          handleSessionExpired();
        }
        return response;
      },
      (error) => {
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status === 401 || isInvalidTokenPayload(data)) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const login = async (username, senha) => {
    const response = await axios.post(`${API}/auth/login`, {
      username,
      senha,
    });

    const authPayload = normalizeAuthPayload(response.data);

    if (!authPayload) {
      throw new Error('Resposta de autenticacao invalida.');
    }

    const { token, usuario } = authPayload;
    const normalizedUser = normalizeUser(usuario);

    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(normalizedUser);

    return normalizedUser;
  };

  // Reemite o token com o escopo atual do banco (canais/perfil) e atualiza a
  // sessao local. Evita ter que deslogar/logar apos mudar canais extras etc.
  const refreshSession = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/auth/me`);
      const authPayload = normalizeAuthPayload(response.data);
      if (!authPayload) return null;

      const { token: newToken, usuario } = authPayload;
      const normalizedUser = normalizeUser(usuario);

      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      setUser(normalizedUser);

      return normalizedUser;
    } catch (error) {
      // 401 ja e tratado pelo interceptor; demais erros nao derrubam a sessao.
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete axios.defaults.headers.common.Authorization;
    setUser(null);
  };

  const temPermissao = (...chaves) => {
    const perms = user?.permissoes || [];
    return chaves.some((c) => perms.includes(c));
  };

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    refreshSession,
    temPermissao,
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export { API, TOKEN_KEY };
